import alertDialogService from "AlertDialogService";
import captionService from "CaptionService";
import { DropDownDisplayMode } from "Constants";
import { DeferredPromise } from "DeferredPromise";
import dialogService, { type Dialog } from "DialogService";
import global from "Global";
import { impersonateUserAsync, ImpersonationResult } from "ImpersonateUserService";
import { loadTemplateAsync } from "ModuleLoader";
import NotificationSummary from "NotificationSummary";
import { NotificationType } from "NotificationType";
import Notifications from "Notifications";
import windowManager from "WindowManager";
import ko, { type Observable, type PureComputed } from "knockout";

export default class ImpersonateUserViewModel {
  private deferred?: DeferredPromise<boolean>;
  private dialogInfo?: Dialog;

  userName: PureComputed<string>;
  orgCode: Observable<string>;
  email: Observable<string>;
  canImpersonate: PureComputed<boolean>;

  readonly DropDownDisplayMode = DropDownDisplayMode;

  readonly notifications: Notifications;
  readonly notificationSummary: NotificationSummary;

  constructor() {
    this.notifications = new Notifications();
    this.notificationSummary = new NotificationSummary(this);

    this.orgCode = ko.observable("");
    this.email = ko.observable("");
    this.userName = ko.pureComputed(() => {
      this.notifications.removeAll();
      return this.orgCode() + "/" + this.email();
    });
    this.canImpersonate = ko.pureComputed(() => {
      return !!this.orgCode() && !!this.email();
    });
  }

  async showImpersonateUserDialogAsync(): Promise<boolean> {
    this.deferred = new DeferredPromise();
    /*! SuppressStringValidation CSS */
    const buttonOptions = [
      {
        caption: dialogService.buttonTypes.Cancel().text,
        result: dialogService.buttonTypes.Cancel().value,
        bindingString: "click: cancel.bind($data)",
        isDismiss: true,
      },
      {
        caption: captionService.getString("389b5abd-2705-4c05-a636-eb652bb55320", "Impersonate"),
        bindingString:
          "asyncClick: impersonateAsync.bind($data), css: { disabled: !canImpersonate }, enable: canImpersonate ",
        isPrimary: true,
      },
    ];

    /*! SuppressStringValidation CSS */
    const dialogCss = "g-dialog-change-impersonate-user";
    this.dialogInfo = await dialogService.showDialogAsync({
      viewModel: this,
      title: captionService.getString("c710540b-5154-4de8-8f2f-f820647ccb38", "Impersonate User"),
      bodyAllowHtml: true,
      bodyDeferred: loadTemplateAsync("ImpersonateContactUser.html"),

      dialogCss,
      closeOnDismissOnly: true,
      buttonOptions,
      includeValidationSummary: !!this.notificationSummary,
    });

    return await this.deferred.promise;
  }

  async impersonateAsync(): Promise<void> {
    this.notifications.removeAll();
    const impersonationResponse = await impersonateUserAsync(this.userName());
    if (impersonationResponse.result === ImpersonationResult.Success) {
      dialogService.hide(this.dialogInfo);
      windowManager.clearListeners();
      windowManager.closeChildWindows();
      global.windowLocation.href = global.getModuleUrl("GHC");
    } else if (impersonationResponse.result === ImpersonationResult.ClaimFailed) {
      this.notifications.push({
        Level: NotificationType.Error,
        propertyName: ".",
        Text: impersonationResponse.errorMessage,
        caption: captionService.getString("4d9bc2c0-5ee1-4f28-91c7-52173d51b8fc", "Error message"),
      });
    } else {
      await alertDialogService.errorWithOKButtonAsync(
        impersonationResponse.errorMessage,
        captionService.getString("dbc80023-ce62-4c58-8436-ff2f5a37fbd7", "Error Impersonating User"),
      );
    }
  }

  cancel(): void {
    this.deferred?.resolve(false);
  }
}
